import React, { useMemo } from "react";
import {
  BackupRounded,
  BusinessRounded,
  DashboardRounded,
  FactCheckRounded,
  FolderOpenRounded,
  HandymanRounded,
  LogoutRounded,
  MeetingRoomRounded,
  PeopleRounded,
  ReceiptRounded,
  RateReviewRounded,
  ManageAccountsRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Avatar,
  Badge,
  alpha,
} from "@mui/material";

type NavItem = {
  key: string;
  label: string;
};

type SidebarProps = {
  items: NavItem[];
  activeView: string;
  onNavigate: (view: string) => void;
  onLogout: () => void;
  userName: string;
  pendingDocumentCount: number;
};

// Extracted outside the component loop to prevent recreation on every render pass
const ICON_COMPONENTS: Record<string, React.ElementType> = {
  dashboard: DashboardRounded,
  properties: BusinessRounded,
  tenants: PeopleRounded,
  "tenant-users": ManageAccountsRounded,
  rooms: MeetingRoomRounded,
  tenancies: ReceiptRounded,
  maintenance: HandymanRounded,
  inspections: FactCheckRounded,
  "document-reviews": RateReviewRounded,
  files: FolderOpenRounded,
  "backup-restore": BackupRounded,
};

export const Sidebar = React.memo(function Sidebar({
  items,
  activeView,
  onNavigate,
  onLogout,
  userName,
  pendingDocumentCount,
}: SidebarProps) {
  // Memoized callback to render icons dynamically without inline instantiation noise
  const renderIcon = useMemo(() => {
    return (key: string, isActive: boolean) => {
      const IconComponent = ICON_COMPONENTS[key] || DashboardRounded;
      return (
        <IconComponent
          sx={{
            fontSize: "1.35rem",
            transition: "color 0.2s ease",
            color: isActive ? "primary.main" : "text.secondary",
          }}
        />
      );
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        width: "100%",
        justifyContent: "space-between",
        p: { xs: 2.25, md: 3 },
        backgroundColor: "background.paper",
        boxSizing: "border-box",
      }}
    >
      <Box>
        {/* Header/Branding */}
        <Box sx={{ mb: 4, px: 1 }}>
          <Typography
            variant="overline"
            color="primary.main"
            sx={{
              fontWeight: 700,
              letterSpacing: "1.2px",
              display: "block",
              lineHeight: 1,
            }}
          >
            HMO Manager
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.5px" }}
          >
            Operations
          </Typography>
        </Box>

        {/* Navigation List */}
        <List disablePadding sx={{ display: "grid", gap: 0.75 }}>
          {items.map((item) => {
            const isActive = activeView === item.key;
            return (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  selected={isActive}
                  onClick={() => onNavigate(item.key)}
                  sx={{
                    borderRadius: 2,
                    py: 1.2,
                    px: 2,
                    transition: "all 0.2s ease-in-out",
                    backgroundColor: "transparent",
                    "&.Mui-selected": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.08),
                      "&:hover": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.primary.main, 0.12),
                      },
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.action.hover, 0.04),
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {renderIcon(item.key, isActive)}
                  </ListItemIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Badge
                      badgeContent={
                        item.key === "document-reviews"
                          ? pendingDocumentCount
                          : 0
                      }
                      color="warning"
                      max={99}
                      invisible={
                        item.key !== "document-reviews" ||
                        pendingDocumentCount === 0
                      }
                      sx={{
                        width: "100%",
                        "& .MuiBadge-badge": { right: 4, top: 10 },
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.925rem",
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? "primary.main" : "text.primary",
                          sx: { transition: "color 0.2s ease" },
                        }}
                      />
                    </Badge>
                  </Box>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Footer Profile & Action section */}
      <Stack
        spacing={2}
        sx={{
          mt: "auto",
          pt: 2.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: "primary.main",
              fontWeight: 600,
              fontSize: "0.9rem",
            }}
          >
            {userName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", lineHeight: 1 }}
            >
              Signed in as
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {userName}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          fullWidth
          startIcon={<LogoutRounded />}
          onClick={onLogout}
          sx={{
            borderRadius: 2,
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            borderColor: "divider",
            color: "text.primary",
            "&:hover": {
              borderColor: "error.main",
              color: "error.main",
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.04),
            },
          }}
        >
          Logout
        </Button>
      </Stack>
    </Box>
  );
});
